import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Manutencao, Veiculo, Motorista } from '../../types';
import { manutencoesService, ManutencaoResponse } from '../../services/manutencoes';
import { veiculosService } from '../../services/veiculos';
import { motoristasService } from '../../services/motoristas';
import CurrencyInput from '../../components/CurrencyInput';
import { KmInput } from '../../components/MaskedInputs';
import SearchableSelect from '../../components/SearchableSelect';
import DicaUltimoKm from '../../components/DicaUltimoKm';
import { erroDeKmMenor, useUltimoKm } from '../../hooks/useUltimoKm';

// Listas de seleção precisam vir completas: o SearchableSelect filtra em memória,
// então o que não veio na resposta é invisível para a busca do usuário.
const OPCOES_SELECT = { per_page: 500 } as const;

const ManutencoesForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    veiculo_id: '',
    motorista_id: '',
    tipo: 'preventiva',
    descricao: '',
    data_manutencao: '',
    quilometragem: '',
    valor_total: '',
    status: 'agendada',
    oficina_nome: '',
    observacoes: '',
    proxima_km: '',
    proxima_manutencao: '',
    garantia_dias: '',
    garantia_vencimento: '',
  });

  // Buscar manutenção para edição
  const { data: manutencaoData, isLoading: isLoadingManutencao } = useQuery<ManutencaoResponse>({
    queryKey: ['manutencao', id],
    queryFn: () => manutencoesService.getManutencao(Number(id!)),
    enabled: isEditing,
    staleTime: 0
  });

  // Buscar veículos para o select
  const { data: veiculosData } = useQuery({
    queryKey: ['veiculos', OPCOES_SELECT],
    queryFn: () => veiculosService.getVeiculos(OPCOES_SELECT),
  });

  // Buscar motoristas para o select
  const { data: motoristasData } = useQuery({
    queryKey: ['motoristas', OPCOES_SELECT],
    queryFn: () => motoristasService.getMotoristas(OPCOES_SELECT),
  });

  // Mutation para criar/atualizar
  const mutation = useMutation({
    mutationFn: (data: Partial<Manutencao>) => {
      if (isEditing) {
        return manutencoesService.updateManutencao(Number(id!), data);
      }
      return manutencoesService.createManutencao(data);
    },
    onSuccess: (response: ManutencaoResponse) => {
      if (response.success) {
        toast.success(isEditing ? 'Manutenção atualizada com sucesso!' : 'Manutenção criada com sucesso!');
        // Invalidar so a lista deixava o detalhe e o proprio formulario
        // servindo o cache anterior a edicao: o registro aparecia atualizado na
        // listagem e desatualizado ao abrir. Sao chaves diferentes, e as duas
        // precisam cair.
        queryClient.invalidateQueries({ queryKey: ['manutencoes'] });
        if (id) {
          queryClient.invalidateQueries({ queryKey: ['manutencao', id] });
        }
        navigate('/manutencoes');
      } else {
        toast.error(response.message || 'Erro ao salvar manutenção');
      }
    },
    onError: (error: any) => {
      toast.error('Erro ao salvar manutenção');
      console.error('Erro:', error);
    },
  });

  // Carregar dados da manutenção para edição
  useEffect(() => {
    if (manutencaoData?.data) {
      const manutencao = manutencaoData.data;
      setFormData({
        veiculo_id: manutencao.veiculo_id?.toString() ?? '',
        motorista_id: manutencao.motorista_id?.toString() ?? '',
        tipo: manutencao.tipo ?? 'preventiva',
        descricao: manutencao.descricao ?? '',
        data_manutencao: manutencao.data_manutencao ? new Date(manutencao.data_manutencao).toISOString().split('T')[0] : '',
        quilometragem: manutencao.quilometragem?.toString() ?? '',
        valor_total: manutencao.valor_total?.toString() ?? '',
        status: manutencao.status ?? 'agendada',
        oficina_nome: manutencao.oficina_nome ?? '',
        observacoes: manutencao.observacoes ?? '',
        proxima_km: manutencao.proxima_km?.toString() ?? '',
        proxima_manutencao: manutencao.proxima_manutencao ? new Date(manutencao.proxima_manutencao).toISOString().split('T')[0] : '',
        garantia_dias: manutencao.garantia_dias?.toString() ?? '',
        garantia_vencimento: manutencao.garantia_vencimento ? new Date(manutencao.garantia_vencimento).toISOString().split('T')[0] : '',
      });
    }
  }, [manutencaoData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Mesmo tratamento do abastecimento: o km vem preenchido e nao pode ser menor.
  const { ultimo: ultimoKm, carregando: buscandoKm } = useUltimoKm(formData.veiculo_id);

  useEffect(() => {
    if (isEditing || !ultimoKm) return;
    setFormData((prev: typeof formData) =>
      prev.quilometragem === '' ? { ...prev, quilometragem: String(Math.round(ultimoKm.km)) } : prev,
    );
  }, [ultimoKm, isEditing]);

  const erroKm = erroDeKmMenor(formData.quilometragem, ultimoKm);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEditing && erroKm) {
      toast.error(erroKm);
      return;
    }

    const toNumber = (v: string) => (v !== '' ? Number(v) : undefined);
    const toText = (v: string) => (v !== '' ? v : undefined);

    // Converter dados para o formato esperado pela API (nomes alinhados as colunas)
    const submitData: Partial<Manutencao> = {
      veiculo_id: toNumber(formData.veiculo_id),
      motorista_id: toNumber(formData.motorista_id),
      tipo: formData.tipo as 'preventiva' | 'corretiva' | 'emergencial' | 'revisao',
      status: formData.status as 'agendada' | 'em_andamento' | 'concluida' | 'cancelada',
      descricao: formData.descricao,
      data_manutencao: formData.data_manutencao,
      quilometragem: toNumber(formData.quilometragem),
      valor_total: toNumber(formData.valor_total),
      // toText devolve undefined quando vazio, como os demais opcionais deste
      // handler. Antes gravava string vazia na coluna.
      oficina_nome: toText(formData.oficina_nome),
      observacoes: toText(formData.observacoes),
      proxima_km: toNumber(formData.proxima_km),
      proxima_manutencao: toText(formData.proxima_manutencao),
      garantia_dias: toNumber(formData.garantia_dias),
      garantia_vencimento: toText(formData.garantia_vencimento),
    };

    mutation.mutate(submitData);
  };

  if (isLoadingManutencao) {
    return (
      <div className="w-full">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Editar manutenção' : 'Nova manutenção'}
          </h1>
          <p className="text-gray-600">
            {isEditing ? 'Atualize as informações da manutenção' : 'Registre uma nova manutenção'}
          </p>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4">Informações Básicas</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SearchableSelect
                label="Veículo"
                required
                placeholder="Selecione um veículo"
                value={formData.veiculo_id}
                onChange={(v) => setFormData(prev => ({ ...prev, veiculo_id: v }))}
                options={(veiculosData?.data ?? []).map((veiculo: Veiculo) => ({
                  value: String(veiculo.id),
                  label: `${veiculo.placa} - ${veiculo.marca} ${veiculo.modelo}`,
                }))}
              />

              <SearchableSelect
                label="Motorista"
                placeholder="Selecione um motorista"
                value={formData.motorista_id}
                onChange={(v) => setFormData(prev => ({ ...prev, motorista_id: v }))}
                options={(motoristasData?.data ?? []).map((motorista: Motorista) => ({
                  value: String(motorista.id),
                  label: motorista.nome,
                }))}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Manutenção *
                </label>
                <select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="preventiva">Preventiva</option>
                  <option value="corretiva">Corretiva</option>
                  <option value="emergencial">Emergencial</option>
                  <option value="revisao">Revisão</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status *
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="agendada">Agendada</option>
                  <option value="em_andamento">Em Andamento</option>
                  <option value="concluida">Concluída</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data da Manutenção *
                </label>
                <input
                  type="date"
                  name="data_manutencao"
                  value={formData.data_manutencao}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <KmInput
                  name="quilometragem"
                  value={formData.quilometragem}
                  onChange={handleInputChange}
                  label="Quilometragem"
                  required
                />
                <DicaUltimoKm ultimo={ultimoKm} erro={isEditing ? null : erroKm} carregando={buscandoKm} />
              </div>

              <CurrencyInput
                name="valor_total"
                value={formData.valor_total}
                onChange={handleInputChange}
                label="Valor Total"
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Oficina
                </label>
                <input
                  type="text"
                  name="oficina_nome"
                  value={formData.oficina_nome}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição *
              </label>
              <textarea
                name="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                required
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Descreva os serviços realizados..."
              />
            </div>
          </div>

          {/* Informações Adicionais */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4">Informações Adicionais</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <KmInput
                name="proxima_km"
                value={formData.proxima_km}
                onChange={handleInputChange}
                label="Próxima Revisão (KM)"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Próxima Revisão (Data)
                </label>
                <input
                  type="date"
                  name="proxima_manutencao"
                  value={formData.proxima_manutencao}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Garantia (dias)
                </label>
                <input
                  type="number"
                  min="0"
                  name="garantia_dias"
                  value={formData.garantia_dias}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Garantia (Vencimento)
                </label>
                <input
                  type="date"
                  name="garantia_vencimento"
                  value={formData.garantia_vencimento}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observações
              </label>
              <textarea
                name="observacoes"
                value={formData.observacoes}
                onChange={handleInputChange}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Observações adicionais..."
              />
            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/manutencoes')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mutation.isPending ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManutencoesForm;
